import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProjetos } from '../hooks/useProjetos';
import { cores } from '../styles/cores';
import { Avatar, BadgeCategoria, BadgeStatus, BarraProgresso, EstadoVazio, Spinner } from '../components/UI';

function CardMetrica({ titulo, valor, descricao, corValor }) {
  return (
    <div
      style={{
        backgroundColor: '#fff',
        border: `1px solid ${cores.card}`,
        borderRadius: 12,
        padding: 18,
      }}
    >
      <div style={{ fontSize: 13, color: cores.textoSecundario, marginBottom: 8 }}>{titulo}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: corValor || cores.texto }}>{valor}</div>
      <div style={{ fontSize: 12, color: cores.textoSecundario, marginTop: 4 }}>{descricao}</div>
    </div>
  );
}

function CardProjeto({ projeto, aoClicar }) {
  const concluida = projeto.status === 'concluida';
  const atrasado = projeto.status === 'atrasado';
  const corBarra = concluida ? cores.sucesso : atrasado ? cores.erro : cores.primario;

  return (
    <div
      onClick={aoClicar}
      style={{
        backgroundColor: '#fff',
        border: `1px solid ${atrasado ? cores.erroFundo : cores.card}`,
        borderRadius: 12,
        padding: 18,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 15, color: cores.texto }}>{projeto.nome}</h3>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <BadgeCategoria categoria={projeto.categoria} />
        </div>
      </div>

      {projeto.descricao && (
        <p
          style={{
            margin: 0,
            fontSize: 13,
            color: cores.textoSecundario,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {projeto.descricao}
        </p>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <BadgeStatus status={projeto.status} />
      </div>

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
          <strong style={{ color: cores.texto }}>{projeto.percentual || 0}%</strong>
          <span style={{ color: cores.textoSecundario }}>
            {projeto.tarefasConcluidas || 0}/{projeto.totalTarefas || 0} tarefas
          </span>
        </div>
        <BarraProgresso percentual={projeto.percentual || 0} cor={corBarra} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: -8 }}>
          {(projeto.membros || []).slice(0, 4).map((membro, indice) => (
            <div key={membro} style={{ marginLeft: indice === 0 ? 0 : -8 }}>
              <Avatar nome={membro} size={28} />
            </div>
          ))}
        </div>
        {projeto.dataPrevisao && (
          <span style={{ fontSize: 12, color: cores.textoSecundario }}>
            📅 Previsão: {projeto.dataPrevisao}
          </span>
        )}
      </div>
    </div>
  );
}

export function Dashboard() {
  const { usuario } = useAuth();
  const { projetos, carregando } = useProjetos();
  const navegar = useNavigate();

  if (carregando) return <Spinner />;

  const total = projetos.length;
  const emAndamento = projetos.filter((p) => p.status === 'em_andamento').length;
  const concluidos = projetos.filter((p) => p.status === 'concluida').length;
  const atrasados = projetos.filter((p) => p.status === 'atrasado').length;

  const projetosAtivos = projetos.filter((p) => p.status !== 'concluida');

  const primeiroNome = (usuario?.nome || '').split(' ')[0] || 'usuário';

  return (
    <div>
      <h1 style={{ margin: '0 0 4px', fontSize: 22, color: cores.texto }}>Olá, {primeiroNome} 👋</h1>
      <p style={{ margin: '0 0 24px', fontSize: 14, color: cores.textoSecundario }}>
        Aqui está um resumo dos seus projetos
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 16,
          marginBottom: 24,
        }}
      >
        <CardMetrica titulo="Total de projetos" valor={total} descricao="projetos cadastrados" />
        <CardMetrica
          titulo="Em andamento"
          valor={emAndamento}
          descricao={total ? `${Math.round((emAndamento / total) * 100)}% do total` : '—'}
          corValor={cores.primario}
        />
        <CardMetrica titulo="Concluídos" valor={concluidos} descricao="projetos finalizados" corValor={cores.sucesso} />
        <CardMetrica
          titulo="Atrasados"
          valor={atrasados}
          descricao={atrasados > 0 ? 'requer atenção' : 'tudo em dia'}
          corValor={atrasados > 0 ? cores.erro : cores.texto}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 16, color: cores.texto }}>Projetos ativos</h2>
        <span
          onClick={() => navegar('/projetos')}
          style={{ fontSize: 13, color: cores.primario, cursor: 'pointer', fontWeight: 600 }}
        >
          Ver todos →
        </span>
      </div>

      {projetosAtivos.length === 0 ? (
        <EstadoVazio
          titulo="Nenhum projeto ativo"
          descricao="Crie um novo projeto para começar a acompanhar o progresso da equipe."
        />
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 16,
          }}
        >
          {projetosAtivos.map((projeto) => (
            <CardProjeto
              key={projeto.id}
              projeto={projeto}
              aoClicar={() => navegar(`/projetos/${projeto.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
